"use client";

import { ColumnDef } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/[lang]/[workspaceId]/_components/table";
import { Skeleton } from "../../../../../components/ui/skeleton";

const getColumnSize = (
  columnDef: unknown,
): { size?: number; minSize?: number } => {
  const def = columnDef as { size?: number; minSize?: number };
  return {
    size: def.size,
    minSize: def.minSize,
  };
};

type Props<TData, TValue> = {
  rowCount: number;
  hasHeader?: boolean;
  columnDefs: ColumnDef<TData, TValue>[];
};

export function TableSkeleton<TData, TValue>({
  rowCount,
  hasHeader = true,
  columnDefs,
}: Props<TData, TValue>) {
  const headers = columnDefs.map((columnDef) => {
    const { size, minSize } = getColumnSize(columnDef);
    return {
      id: columnDef.id ?? Math.random().toString(),
      width: size,
      minWidth: minSize,
    };
  });

  return (
    <div className="rounded-md">
      <Table style={{ tableLayout: "fixed" }}>
        {hasHeader && (
          <TableHeader>
            <TableRow>
              {headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.width ? `${header.width}px` : "80px",
                    minWidth: header.minWidth
                      ? `${header.minWidth}px`
                      : undefined,
                  }}
                  className="whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  <Skeleton className="h-6 w-full" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        )}
        <TableBody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <TableRow key={i}>
              {headers.map((header) => (
                <TableCell
                  key={`${header.id}-${i}`}
                  className="whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
